import { apiErrorFromResponse } from '@multaqa/api-client'
import { fetchWithSessionActivity } from './api'

/**
 * Download a file over an authenticated GET.
 *
 * Not an `<a href>`. The report-export endpoints need the `X-Company-Id`
 * header to know which tenant is asking, and a browser navigation cannot send
 * one — so the request goes through `fetch` (credentials included, so the
 * Sanctum session cookie rides along) and the bytes are handed to the browser
 * as a blob download.
 *
 * Extracted from `features/hr-attachments/api.ts`'s `downloadAttachment`,
 * which is feature-internal: a feature may not import another feature
 * (AGENTS.md), so the mechanical part — fetch, error check, blob, anchor —
 * lives here in `lib/` and each caller supplies its own URL and headers.
 *
 * @param url          Absolute URL including the `/api` prefix.
 * @param fallbackName The filename to download as when the server's
 *                     `Content-Disposition` names none.
 * @param headers      Extra headers, e.g. `{ 'X-Company-Id': companyId }`.
 */
export async function downloadFile(
  url: string,
  fallbackName: string,
  headers: Record<string, string> = {},
): Promise<void> {
  const response = await fetchWithSessionActivity(url, {
    method: 'GET',
    credentials: 'include',
    headers,
  })

  if (!response.ok) {
    throw await apiErrorFromResponse(response)
  }

  const objectUrl = URL.createObjectURL(await response.blob())

  try {
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download =
      filenameFromDisposition(response.headers.get('content-disposition')) ?? fallbackName
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

/**
 * The RFC 5987-era pattern Laravel's file download response sets:
 * `attachment; filename="name.csv"` (optionally with `filename*=UTF-8''…`
 * for non-ASCII names — we prefer the plain `filename` so the browser gets
 * the same bytes the API generated).
 */
function filenameFromDisposition(disposition: string | null): string | null {
  if (!disposition) return null
  const match = /filename="?([^";]+)"?/i.exec(disposition)
  return match?.[1] ?? null
}

/**
 * Fetch a document over an authenticated GET and open it in a new tab for
 * printing, rather than saving it to disk.
 *
 * The same `fetch` reason as `downloadFile`: the export endpoints need
 * `X-Company-Id`, and a browser navigation cannot send a header. The blob URL
 * is handed to a new tab whose PDF viewer offers the print dialog, so "print
 * this report" is one click and the printed page is the SERVER's branded
 * document — not the screen's table with the app chrome sheared off, which is
 * what a `window.print()` of the SPA would produce.
 *
 * The object URL is revoked on a timer, not immediately: revoking it before
 * the new tab has finished reading it leaves a blank viewer. A minute is far
 * longer than any load and still bounds the leak.
 */
export async function openFileInNewTab(
  url: string,
  headers: Record<string, string> = {},
): Promise<void> {
  const response = await fetchWithSessionActivity(url, {
    method: 'GET',
    credentials: 'include',
    headers,
  })

  if (!response.ok) {
    throw await apiErrorFromResponse(response)
  }

  const objectUrl = URL.createObjectURL(await response.blob())
  const opened = window.open(objectUrl, '_blank', 'noopener,noreferrer')

  if (opened === null) {
    // Pop-up blocked. Falling back to a download beats failing silently.
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = 'document.pdf'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  }

  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
}
